import { useState, useEffect, useContext } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../../utils/auth";
import { CreditCard, Loader2, Wallet } from "lucide-react";
import { API_BASE } from "../../shared/Constants";
import { PaymentContext } from "../../contexts/PaymentContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { reportHandledError, reportApiError } from "../../utils/reportError";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY as string).catch(
  (err) => {
    console.warn("Failed to load Stripe.js:", err?.message || err);
    return null;
  }
);

export interface CheckoutProps {
  setup: boolean;
}

function CheckoutForm({
  setup,
  onComplete,
  onError,
  userEmail,
}: {
  setup: boolean;
  onComplete: () => void;
  onError: (msg: string) => void;
  userEmail?: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    try {
      let result;
      const billingDetails = userEmail ? { email: userEmail } : undefined;
      const confirmParams = billingDetails
        ? { payment_method_data: { billing_details: billingDetails } }
        : undefined;

      if (setup) {
        result = await stripe.confirmSetup({
          elements,
          redirect: "if_required",
          confirmParams,
        });
      } else {
        result = await stripe.confirmPayment({
          elements,
          redirect: "if_required",
          confirmParams,
        });
      }

      if (result.error) {
        onError(result.error.message || "Erro no pagamento");
      } else {
        onComplete();
      }
    } catch (err: any) {
      reportHandledError(err, { category: "payment", feature: "checkout.confirm" });
      onError(err.message || "Erro inesperado");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <PaymentElement
        options={{
          layout: "tabs",
          fields: { billingDetails: { email: "never" } },
        }}
      />
      <Button type="submit" disabled={!stripe || processing} className="w-full gap-2">
        {processing ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Processando...
          </>
        ) : setup ? (
          <>
            <CreditCard className="h-4 w-4" />
            Salvar cartão
          </>
        ) : (
          <>
            <Wallet className="h-4 w-4" />
            Confirmar pagamento
          </>
        )}
      </Button>
    </form>
  );
}

export default function Checkout({ setup }: CheckoutProps) {
  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState<string>("");
  const [amountConfirmed, setAmountConfirmed] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const { isDarkMode } = useContext(ThemeContext);
  const [stripeLoadFailed, setStripeLoadFailed] = useState(false);
  useEffect(() => {
    stripePromise.then((s) => {
      if (!s) setStripeLoadFailed(true);
    });
  }, []);
  const {
    setPaymentStage,
    setPaymentToast,
    paymentOption,
    paymentMethod,
    billingScope,
    activeOrganizationId,
  } = useContext(PaymentContext);

  const buildBillingUrl = (path: string, params: Record<string, string>) => {
    const search = new URLSearchParams(params);
    if (billingScope === "organization" && activeOrganizationId) {
      search.set("organization_id", activeOrganizationId);
    }
    return `${API_BASE}/api/billing${path}?${search.toString()}`;
  };

  const formatValue = (inputValue: string) => {
    inputValue = inputValue.replace(/\D/g, "");
    if (inputValue.length > 8) inputValue = inputValue.slice(0, 8);
    while (inputValue.length < 3) inputValue = "0" + inputValue;
    const reals = inputValue.slice(0, -2);
    const cents = inputValue.slice(-2);
    return `${parseInt(reals, 10)}.${cents}`;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatValue(e.target.value);
    if (parseFloat(formatted) > 30000) {
      setInputMessage("O valor máximo é R$ 30.000,00");
      setAmount("30000.00");
    } else {
      setInputMessage("");
      setAmount(formatted);
    }
  };

  const fetchClientSecret = async (amountValue?: string) => {
    setLoading(true);
    try {
      const paramsObj: Record<string, string> = {
        embedded: "true",
        save_card: setup.toString(),
      };
      if (paymentMethod !== "") paramsObj.payment_method = paymentMethod;
      if (!setup && amountValue) paramsObj.amount = amountValue;

      const url = buildBillingUrl("/checkout", paramsObj);
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Auth ${access_token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setClientSecret(data.client_secret);
      } else {
        reportApiError(response, { feature: "checkout.intent", method: "GET" });
        setPaymentToast({
          type: "error",
          message: "Não foi possível iniciar o pagamento. Tente novamente.",
        });
        setPaymentStage("payment");
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "checkout.intent" });
      setPaymentToast({
        type: "error",
        message: "Erro durante o processamento. Tente novamente mais tarde.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (setup) fetchClientSecret();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAmountSubmit = () => {
    const val = parseFloat(amount);
    if (isNaN(val) || val < 20) {
      setInputMessage("O valor mínimo é R$ 20,00");
      return;
    }
    setAmountConfirmed(true);
    fetchClientSecret(amount);
  };

  const handleComplete = () => {
    if (setup) {
      if (paymentOption === "single") {
        setPaymentToast({ type: "success", message: "Cartão adicionado com sucesso!" });
        setPaymentStage("payment");
      } else {
        setPaymentStage("methods");
      }
    } else {
      setPaymentToast({ type: "success", message: "Pagamento efetuado com sucesso!" });
    }
  };

  const handleError = (msg: string) => {
    setPaymentToast({ type: "error", message: msg });
  };

  if (!setup && !amountConfirmed) {
    return (
      <div className="flex flex-col gap-5 p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-foreground">
            <Wallet className="h-[18px] w-[18px]" />
          </span>
          <div>
            <h3 className="text-[15px] font-semibold tracking-tight text-foreground">
              Adicionar créditos
            </h3>
            <p className="text-[13px] text-muted-foreground">
              Escolha o valor a ser carregado em seu saldo
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[13px] font-semibold text-foreground">Valor da recarga</label>
          <div className="flex items-center overflow-hidden rounded-md border border-border focus-within:border-ring focus-within:ring-1 focus-within:ring-ring">
            <span className="border-r border-border bg-muted px-3 py-2.5 text-sm font-semibold text-muted-foreground">
              R$
            </span>
            <input
              type="text"
              placeholder="0,00"
              value={amount}
              onChange={handleAmountChange}
              autoFocus
              className="flex-1 bg-background px-3 py-2.5 text-base font-semibold text-foreground outline-none placeholder:font-normal placeholder:text-muted-foreground"
            />
          </div>
          {inputMessage && <p className="text-[12px] font-medium text-destructive">{inputMessage}</p>}
        </div>

        <div className="flex flex-wrap gap-2">
          {[20, 30, 50, 100, 200].map((val) => (
            <button
              key={val}
              type="button"
              onClick={() => {
                setAmount(val.toFixed(2));
                setInputMessage("");
              }}
              className="rounded-full border border-border bg-background px-3 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
            >
              R$ {val}
            </button>
          ))}
        </div>

        <Button
          onClick={handleAmountSubmit}
          disabled={!amount || parseFloat(amount) === 0}
          className="w-full"
        >
          Continuar para pagamento
        </Button>
      </div>
    );
  }

  if (loading || !clientSecret) {
    return (
      <div className="flex flex-col gap-3 p-6">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-11 w-full" />
        <div className="flex gap-3">
          <Skeleton className="h-11 w-1/2" />
          <Skeleton className="h-11 w-1/2" />
        </div>
        <Skeleton className="h-3 w-2/5" />
        <Skeleton className="h-11 w-full" />
        <Skeleton className="mt-1 h-12 w-full" />
      </div>
    );
  }

  if (stripeLoadFailed) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 px-6 py-8 text-center text-muted-foreground">
        <p>Não foi possível carregar o sistema de pagamentos.</p>
        <p className="text-[13px] opacity-70">
          Verifique se extensões do navegador (como bloqueadores de anúncios) estão impedindo o
          carregamento e tente novamente.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: isDarkMode ? "night" : "stripe",
            variables: {
              colorPrimary: "#1a3c34",
              borderRadius: "8px",
              fontFamily: "Inter, system-ui, sans-serif",
              ...(isDarkMode
                ? {
                    colorBackground: "#0a0e1a",
                    colorText: "#e2e8f0",
                    colorTextSecondary: "#94a3b8",
                  }
                : {}),
            },
          },
        }}
      >
        <CheckoutForm
          setup={setup}
          onComplete={handleComplete}
          onError={handleError}
          userEmail={user?.email}
        />
      </Elements>
    </div>
  );
}
